import 'es6-shim'
import { AuthService, fetchUserRegion } from './base/auth/AuthService'
import { ClientConfig } from './base/config/ClientConfig'
import { Injector } from './base/injector'
import { myPlayer } from './base/MyPlayer'
import { MfaService } from './base/auth/MfaService'
import { setupPlugins } from './includes'
import { Utils } from './base/Utils'
import { Settings } from './base/Settings'
import { AuthError } from './base/auth/AuthError'
import { appCtx } from './base/ApplicationContext'
import { rmpProfilingPage } from './base/ui/cashier/RiskManagementPlatformProfiler'
import { performLoginRiskAssessment } from './base/communication/customer/CustomerDueDiligenceApi'
import { loginCompleted } from './base/redux/cashier/UserActions'
import { FeatureToggleService } from './base/services/FeatureToggleService'
import { LogManager } from './base/LogManager'
import { hydrateGeoStateFromSessionStorage } from './base/services/geo/boot-geo-hydration'
import { isGeoDebugMode } from './base/services/geo/geo-debug-utils'
import { registerGeoSink, emitGeoEvent, setGeoContext, GeoEvent } from './base/services/geo/geo-analytics'
import { logManagerSink, fullStorySink } from './base/services/geo/geo-analytics-sinks'
import { detectPlatform } from './base/services/geo/geo-platform'
import { registerLoginSink, emitLoginEvent, setLoginContext, LoginEvent } from './base/services/login/login-analytics'
import { logManagerSink as loginLogManagerSink, fullStorySink as loginFullStorySink } from './base/services/login/login-analytics-sinks'
import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { BootErrorScreen } from './BootErrorScreen'

function showBootError(message: string): void {
	const progress = document.querySelector<HTMLElement>('.loading-progress')
	if (progress) progress.style.display = 'none'
	const container = document.createElement('div')
	document.getElementById('loadingView')?.appendChild(container)
	ReactDOM.render(<BootErrorScreen message={message} />, container)
}

const recordViewHeight = () => {
	const vh = window.innerHeight * 0.01
	if (vh > 0) {
		document.documentElement.style.setProperty('--vh', `${vh}px`)
	}
}

setupPlugins()

if (myPlayer) globalMyPlayer = myPlayer

recordViewHeight()
window.addEventListener('resize', recordViewHeight)

legacy_ga._trackEvent('client_initiation', 'document_ready')

if (Poker.SkinConfiguration.onLoad) {
	Poker.SkinConfiguration.onLoad()
}

initialiseApp()

async function initialiseApp() {
	// Register analytics sinks before any flow runs
	registerGeoSink(logManagerSink)
	registerGeoSink(fullStorySink)
	registerLoginSink(loginLogManagerSink)
	registerLoginSink(loginFullStorySink)
	LogManager.setPlayerIdResolver(() => AuthService.getCubeiaIdSynchronous())

	// Geo-debug: attempt auth silently, show debug UI regardless of outcome.
	// Avoids running the full authenticateUser() flow which triggers Auth0 SDK's
	// internal checkSession iframe — that leaks an unhandled login_required rejection
	// when there is no active session.
	if (FeatureToggleService.geoDebugPanelFeature.isEnabled() && isGeoDebugMode()) {
		let isAuthenticated = false
		try {
			const urlParams = Utils.getUrlParams()
			await AuthService.prepare({ from: urlParams.from, referral: urlParams.referral, suppressAutoLogin: true })
			isAuthenticated = await AuthService.isAuthenticated()
		} catch {
			// Auth unavailable — expected when not logged in
		}
		const { mountGeoDebugPanel } = await import('./base/ui/geo-debug/mount')
		await mountGeoDebugPanel(isAuthenticated)
		return
	}

	setLoginContext({ platform: detectPlatform(), buildVersion })
	emitLoginEvent(LoginEvent.BOOT_STARTED, {})
	try {
		await Promise.all([authenticateUser(), initialisei18n()])
	} catch (error) {
		LogManager.error({ message: 'An error occurred during initialisation', error: error as Error })
		if (error.name === 'AuthError') {
			return
		}
	}

	emitLoginEvent(LoginEvent.BOOT_COMPLETED, {})
	const cubeiaId = AuthService.getCubeiaIdSynchronous()
	if (cubeiaId !== -1) {
		(window as any).FS?.identify(cubeiaId)
	}

	// Show lobby skeleton behind loading bar (feature-toggled)
	const skeleton = FeatureToggleService.lobbySkeletonFeature.isEnabled()
		? document.getElementById('lobby-skeleton')
		: null
	skeleton?.classList.add('visible')

	// Show loading until main app mounts
	document.getElementById('loadingView')!.style.display = ''

	// Resolve userId for canary percentage check before evaluating the geo toggle
	let userId: string | undefined
	try {
		userId = await AuthService.getCurrentUserId()
		FeatureToggleService.geoComplyFeature.setUserId(Number(userId))
	} catch {
		LogManager.warn({ message: 'Failed to resolve userId for canary check' })
	}

	setGeoContext({ platform: detectPlatform(), jwtGeo: await fetchUserRegion() })

	// Phase 2: Geo (when toggle ON)
	const geoEnabled = FeatureToggleService.geoComplyFeature.isEnabled()
	emitGeoEvent(GeoEvent.ROLLOUT_EVALUATED, { inGroup: geoEnabled })
	if (geoEnabled) {
		try {
			const resolvedUserId = userId ?? await AuthService.getCurrentUserId()
			const { runConsentGate } = await import(/* webpackChunkName: "poker-client-geo", webpackMode: "lazy" */ './base/services/geo/consent-gate')
			await runConsentGate(resolvedUserId)
			const { runGeoGate } = await import(/* webpackChunkName: "poker-client-geo", webpackMode: "lazy" */ './geo-gate-init')
			const result = await runGeoGate()
			if (result === 'failed') return
			if (result === 'restricted') return
		} catch (error) {
			LogManager.error({ message: 'Phase 2 (geo-gate) failed', error: error as Error })
			showBootError(i18n.t('boot-error.message'))
			return
		}
	}

	if (geoEnabled) {
		hydrateGeoStateFromSessionStorage(window.sessionStorage)
	}

	// Phase 3: Load main app (dynamic import)
	emitLoginEvent(LoginEvent.APP_REQUESTED, {})
	let signalAppReady: (() => void) | undefined
	let appInitSucceeded = false
	try {
		const appInit = await import(/* webpackChunkName: "poker-client-main", webpackMode: "lazy" */ './app-init')
		signalAppReady = appInit.signalAppReady
		await appInit.initApp()
		appInitSucceeded = true
		emitLoginEvent(LoginEvent.APP_MOUNTED, {})
		emitGeoEvent(GeoEvent.LOBBY_LOADED, { inGroup: geoEnabled })

		// Hide loading overlay in the same frame as skeleton fade-out to prevent
		// a flash of the raw skeleton between loading bar removal and lobby paint.
		const loginView = document.getElementById('loginView')
		if (loginView) loginView.style.display = 'none'
	} catch (error) {
		LogManager.error({ message: 'Phase 3 (main app) failed', error: error as Error })
		showBootError(i18n.t('boot-error.message'))
	} finally {
		// Don't signal ready on a failed boot — misleading to callers.
		if (appInitSucceeded) {
			if (skeleton) {
				const blur = document.getElementById('lobby-skeleton-blur')
				skeleton.classList.add('fade-out')
				blur?.classList.add('fade-out')
				// Fallback: transitionend may not fire (e.g. prefers-reduced-motion).
				let signalled = false
				const fireSignal = () => {
					if (!signalled) {
						signalled = true
						skeleton.remove()
						blur?.remove()
						signalAppReady?.()
					}
				}
				skeleton.addEventListener('transitionend', fireSignal, { once: true })
				setTimeout(fireSignal, 2000)
			} else {
				signalAppReady?.()
			}
		}
	}
}

async function initialisei18n() {
	try {
		await new Promise<void>(resolve => {
			i18n.init(
				{
					fallbackLng: 'en',
					postProcess: 'sprintf',
					resGetPath: `${contextPath}/i18n/__lng__-${buildVersion}.json`,
				},
				async () => {
					$('body').i18n()
					resolve()
				})
		})
		emitLoginEvent(LoginEvent.TRANSLATIONS_LOADED, {})
	} catch (error) {
		LogManager.error({ message: 'Error initialising i18n', error: error as Error })
	}
}

async function authenticateUser() {
	emitLoginEvent(LoginEvent.AUTH_STARTED, {})

	if (window.location.search.includes('error=')) {
		emitLoginEvent(LoginEvent.AUTH_FAILED, { reason: 'error_param' })
		LogManager.flush().catch((e) => { console.warn('[boot] LogManager.flush failed during error redirect', e) })
		window.location.href = `${window.location.origin}/user/error${window.location.search}`
		throw new AuthError('An error condition is present')
	}

	try {
		const urlParams = Utils.getUrlParams()
		await AuthService.prepare({
			from: urlParams.from,
			referral: urlParams.referral
		})
	} catch (e: unknown) {
		const errorMessage = '[initialisation-error] Unable to initialise Auth0 SDK'
		LogManager.error({ message: `${errorMessage}: ${(e as Error).message}`, error: e as Error })
		emitLoginEvent(LoginEvent.AUTH_FAILED, { reason: 'sdk_init' })
		LogManager.flush().catch((e) => { console.warn('[boot] LogManager.flush failed during error redirect', e) })
		window.location.href = `${window.location.origin}/user/error?error_description=${encodeURIComponent(errorMessage)}`
		throw new AuthError(errorMessage)
	}

	let authMethod: 'redirect_callback' | 'silent' | 'override' = 'silent'
	let authCompleteEmitted = false

	const disableMfa = window.location.search.includes('disable-mfa=true')
	if (window.location.search.includes('code=')) {
		authMethod = 'redirect_callback'
		let callbackSucceeded = false
		try {
			await AuthService.handleRedirectCallback()
			callbackSucceeded = true
		} catch (e: unknown) {
			const errorMessage = '[initialisation-error] Unable to handle redirect callback from Auth0'
			const response = await AuthService.getTokenSilently();
			if (!response || response.length === 0) {
				emitLoginEvent(LoginEvent.AUTH_FAILED, { reason: 'redirect_callback' })
				LogManager.flush().catch((e) => { console.warn('[boot] LogManager.flush failed during error redirect', e) })
				window.location.href = `${window.location.origin}/user/error?error_description=${encodeURIComponent(errorMessage)}`
				throw new AuthError(errorMessage)
			}
		}

		assessLoginRiskWithRmp()

		if (disableMfa) {
			await MfaService.disableMfa()
		}
		// When callback succeeded, it did replaceState with returnTo — preserve pathname + search.
		// When callback threw but getTokenSilently recovered, search still has code/state — strip them to avoid refresh loop.
		const targetUrl = callbackSucceeded
			? window.location.pathname + (window.location.search || '')
			: window.location.pathname
		window.history.replaceState({}, document.title, targetUrl)
		window.history.pushState({}, document.title, targetUrl)
		loginCompleted()
		authCompleteEmitted = true
		emitLoginEvent(LoginEvent.AUTH_COMPLETED, { authMethod })
	}

	if (ClientConfig.OVERRIDE_JWT) {
		authMethod = 'override'
		let id
		while (!id || id < 1 || id > 50) {
			id = Number(window.prompt('Enter user id (1 to 50)', '1'))
		}

		await fetch('/jwt/' + id)
			.then(r => r.text())
			.then(t => myPlayer.fakeJwtToken = t)
		if (!authCompleteEmitted) {
			authCompleteEmitted = true
			emitLoginEvent(LoginEvent.AUTH_COMPLETED, { authMethod })
		}
	} else {
		const isAuthenticated = await AuthService.isAuthenticated()
		if (!isAuthenticated) {
			// #POK-5723 Player is unable to access gameClient upon hitting the This page isn't working
			// error (error 431)
			// Related to issue: https://github.com/auth0/auth0-spa-js/issues/217#issuecomment-537285705
			// If the crumbs keep getting appended, forcefully remove them after count > 5
			clearAuth0CookieCrumbs()
			// Reset currency upon login (only if they had to log in e.g. session expired)
			Utils.remove(Settings.Param.CURRENCY_SELECTED)
			const returnTo = window.location.pathname + (window.location.search || '') || '/'
			try {
				await AuthService.loginWithRedirect({ appState: { returnTo } })
				loginCompleted()
			} catch (e) {
				emitLoginEvent(LoginEvent.AUTH_FAILED, { reason: 'login_redirect' })
				LogManager.flush().catch((e) => { console.warn('[boot] LogManager.flush failed during error redirect', e) })
				throw e
			}
			return
		}
		if (!authCompleteEmitted) {
			emitLoginEvent(LoginEvent.AUTH_COMPLETED, { authMethod })
		}
	}
}

// Debug globals: local dev only. Use $r in React DevTools to inspect components.
if (process.env.POK_STAGE === 'local') {
	(window as any).rootInjector = Injector.root;
	(window as any).appCtx = appCtx
}

/**
 * If the auth0 crumbs keep getting appended, forcefully remove them.
 */
function clearAuth0CookieCrumbs() {
	const a0cookies = document.cookie.split(';').filter(cookie => cookie.indexOf('a0.spajs.txs') !== -1)
	if ( a0cookies.length <= 5 ) return

	const cookiesToDelete = a0cookies.splice(5, a0cookies.length - 1)
	cookiesToDelete.forEach(deleteCookie)
}

const deleteCookie = (cookie: string) => {
	const cookieName = cookie.split('=')[0].trim()
	document.cookie = cookieName + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT'
}

const assessLoginRiskWithRmp = async () => {
	AuthService.getCurrentUserId().then(pokId => {
		rmpProfilingPage().then(rawProfilingBundle => {
			performLoginRiskAssessment(rawProfilingBundle)
				.then((response) => {
					const recommendedAction = JSON.parse(response).recommendedAction
					LogManager.metric({
						message: `Login risk assessment result of action[${recommendedAction.toUpperCase()}] for pokId[${pokId}]`,
						action: recommendedAction.toUpperCase(),
						pokId,
					})
				})
				.catch(error => {
					LogManager.error({
						message: `Error performing login risk assessment for pokId[${pokId}]`,
						error,
					})
				})
		}).catch(error => {
			LogManager.error({
				message: `[RMP] Error loading RMP profiling page for pokId[${pokId}]`,
				error,
			})
		})
	})
}

if (module.hot) {
	module.hot.accept()
}
